/* eslint-disable i18next/no-literal-string */
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Pressable, ScrollView, TextInput, View } from 'react-native'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2, Heading3 } from '~/components/typography/Titles'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'
import { cn } from '~/lib/utils'

type FeedbackStatus = 'pending' | 'responded' | 'rejected'
type StatusFilter = FeedbackStatus | 'all'

type DateRangeOption = {
  name: string
  startDate: string
  endDate: string
}

const now = dayjs()

const DATE_RANGE_OPTIONS: DateRangeOption[] = [
  { name: 'Today', startDate: now.startOf('day').toISOString(), endDate: now.toISOString() },
  {
    name: 'Last 7 days',
    startDate: now.subtract(7, 'day').startOf('day').toISOString(),
    endDate: now.toISOString(),
  },
  {
    name: 'Last 30 days',
    startDate: now.subtract(30, 'day').startOf('day').toISOString(),
    endDate: now.toISOString(),
  },
  {
    name: 'Last 3 months',
    startDate: now
      .month(Math.floor(now.month() / 3) * 3)
      .startOf('month')
      .toISOString(),
    endDate: now.toISOString(),
  },
  {
    name: 'Last 6 months',
    startDate: now.subtract(6, 'month').startOf('month').toISOString(),
    endDate: now.toISOString(),
  },
  { name: 'This week', startDate: now.startOf('week').toISOString(), endDate: now.toISOString() },
  { name: 'This month', startDate: now.startOf('month').toISOString(), endDate: now.toISOString() },
  { name: 'This year', startDate: now.startOf('year').toISOString(), endDate: now.toISOString() },
]

type ObjectDetail = {
  title: string
  fields: { label: string; value?: string | null }[]
}

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) {
    return null
  }

  return (
    <View className="gap-1 py-2">
      <Label className="text-xs text-secondary">{label}</Label>
      <Text className="text-sm">{value}</Text>
    </View>
  )
}

function StatusBadge({ status }: { status: FeedbackStatus }) {
  const config: Record<FeedbackStatus, { label: string; className: string }> = {
    pending: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
    responded: { label: 'Responded', className: 'bg-green-100 text-green-700' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
  }
  const { label, className } = config[status]

  return (
    <View className={cn('rounded-full px-2 py-0.5', className)}>
      <Text className="font-medium text-xs" numberOfLines={1}>
        {label}
      </Text>
    </View>
  )
}

function StatCard({
  label,
  value,
  percent,
  colorClass,
}: {
  label: string
  value: number
  percent: number | null
  colorClass: string
}) {
  return (
    <View className="flex-1 gap-1 rounded-xl bg-white p-4">
      <View className={`mb-1 h-1 w-8 rounded-full ${colorClass}`} />
      <Text className="font-bold text-2xl">{value}</Text>
      <Label className="text-xs text-secondary">{label}</Label>
      {percent !== null && (
        <Label className="font-medium text-xs text-neutral-500">{percent.toFixed(1)}%</Label>
      )}
    </View>
  )
}

function FilterChip({
  label,
  active,
  onPress,
}: {
  label: string
  active: boolean
  onPress: () => void
}) {
  return (
    <Pressable
      onPress={onPress}
      className={`rounded-full px-3 py-1.5 ${active ? 'bg-neutral-900' : 'border border-neutral-200 bg-white'}`}>
      <Text className={`font-medium text-xs ${active ? 'text-white' : 'text-neutral-600'}`}>
        {label}
      </Text>
    </Pressable>
  )
}

const CHART_COLORS = {
  sent: '#18181b',
  opened: '#6366f1',
  responded: '#16a34a',
  rejected: '#dc2626',
}

const PAGE_SIZE = 50

export default function FeedbackStats() {
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption | null>(null)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>()
  const [selectedUserName, setSelectedUserName] = useState<string>('')
  const [userSearch, setUserSearch] = useState('')
  const [userSearchFocused, setUserSearchFocused] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedObject, setSelectedObject] = useState<ObjectDetail | null>(null)

  const statsQuery = useQuery(
    trpc.admin.adminGetFeedbackStats.queryOptions(
      {
        userId: selectedUserId,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate,
      },
      {
        placeholderData: keepPreviousData,
      }
    )
  )

  const feedbackQuery = useQuery(
    trpc.admin.adminListAllFeedback.queryOptions(
      {
        page,
        limit: PAGE_SIZE,
        userId: selectedUserId,
        startDate: selectedDateRange?.startDate,
        endDate: selectedDateRange?.endDate,
        status: statusFilter === 'all' ? undefined : statusFilter,
      },
      {
        placeholderData: keepPreviousData,
      }
    )
  )

  const usersQuery = useQuery(
    trpc.admin.listUsersForAdmin.queryOptions(
      {
        limit: 20,
        search: userSearch,
        role: 'realtor',
      },
      {
        placeholderData: keepPreviousData,
      }
    )
  )

  const stats = statsQuery.data
  const feedbackItems = feedbackQuery.data?.items ?? []
  const totalPages = feedbackQuery.data?.totalPages ?? 1
  const totalCount = feedbackQuery.data?.totalCount ?? 0

  const sentTotal = stats?.totalSent ?? 0

  const chartData = stats
    ? [
        { name: 'Sent', value: stats.totalSent, fill: CHART_COLORS.sent },
        { name: 'Opened', value: stats.totalOpened, fill: CHART_COLORS.opened },
        { name: 'Responded', value: stats.totalResponded, fill: CHART_COLORS.responded },
        { name: 'Rejected', value: stats.totalRejected, fill: CHART_COLORS.rejected },
      ]
    : []

  const toPercent = (value: number) => (sentTotal > 0 ? (value / sentTotal) * 100 : 0)

  const handleUserSelect = (userId: string, name: string) => {
    setSelectedUserId(userId)
    setSelectedUserName(name)
    setUserSearch('')
    setUserSearchFocused(false)
    setPage(1)
  }

  const handleClearUser = () => {
    setSelectedUserId(undefined)
    setSelectedUserName('')
    setUserSearch('')
    setPage(1)
  }

  const handleDateRange = (option: DateRangeOption | null) => {
    setSelectedDateRange(option)
    setPage(1)
  }

  const handleStatusFilter = (value: StatusFilter) => {
    setStatusFilter(value)
    setPage(1)
  }

  const showUserDropdown =
    userSearchFocused && userSearch.length > 0 && (usersQuery.data?.users?.length ?? 0) > 0

  return (
    <ScreenView className="mx-auto" innerClassName="max-w-4xl">
      <ContentBox
        className="mx-auto max-w-4xl flex-1"
        contentContainerClassName="gap-6"
        variant="transparent">
        <Heading2 className="text-center">Feedback Stats</Heading2>

        <View className="gap-3 rounded-xl bg-white p-4" style={{ overflow: 'visible' }}>
          <View style={{ position: 'relative', zIndex: 10, overflow: 'visible' }}>
            <View className="flex-row items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
              {selectedUserName ? (
                <>
                  <Text className="flex-1 text-sm">{selectedUserName}</Text>
                  <Pressable onPress={handleClearUser} className="rounded px-2 py-0.5">
                    <Text className="text-xs text-neutral-400">✕ Clear</Text>
                  </Pressable>
                </>
              ) : (
                <TextInput
                  value={userSearch}
                  onChangeText={setUserSearch}
                  onFocus={() => setUserSearchFocused(true)}
                  onBlur={() => setTimeout(() => setUserSearchFocused(false), 150)}
                  placeholder="Filter by realtor..."
                  className="flex-1 text-sm text-neutral-900 outline-none"
                  style={{ fontFamily: 'System' }}
                />
              )}
            </View>

            {showUserDropdown && (
              <View
                className="rounded-lg border border-neutral-200 bg-white shadow-md"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  zIndex: 9999,
                }}>
                {(usersQuery.data?.users ?? []).map((user) => (
                  <Pressable
                    key={user.userId}
                    onPress={() => handleUserSelect(user.userId, user.name)}
                    className="border-b border-neutral-100 px-3 py-2.5 last:border-b-0 active:bg-neutral-50">
                    <Text className="text-sm">{user.name}</Text>
                    {!!user.email && <Label className="text-xs text-secondary">{user.email}</Label>}
                  </Pressable>
                ))}
              </View>
            )}
          </View>

          <View className="flex-row flex-wrap gap-2">
            <Label className="mr-1 self-center text-xs text-secondary">Period:</Label>
            <FilterChip
              label="All time"
              active={selectedDateRange === null}
              onPress={() => handleDateRange(null)}
            />
            {DATE_RANGE_OPTIONS.map((option) => (
              <FilterChip
                key={option.name}
                label={option.name}
                active={selectedDateRange?.name === option.name}
                onPress={() => handleDateRange(option)}
              />
            ))}
          </View>
        </View>

        {statsQuery.isLoading && (
          <Body className="text-center text-neutral-500">Loading stats...</Body>
        )}

        {!!stats && (
          <>
            <View className="flex-row gap-3">
              <StatCard
                label="Sent"
                value={stats.totalSent}
                percent={null}
                colorClass="bg-neutral-900"
              />
              <StatCard
                label="Opened"
                value={stats.totalOpened}
                percent={toPercent(stats.totalOpened)}
                colorClass="bg-indigo-500"
              />
              <StatCard
                label="Responded"
                value={stats.totalResponded}
                percent={toPercent(stats.totalResponded)}
                colorClass="bg-green-600"
              />
              <StatCard
                label="Rejected"
                value={stats.totalRejected}
                percent={toPercent(stats.totalRejected)}
                colorClass="bg-red-600"
              />
            </View>

            <View className="rounded-xl bg-white p-4">
              <Heading3 className="mb-4">Conversion Funnel</Heading3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 16, right: 32 }}>
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={80}
                    tick={{ fontSize: 13, fill: '#71717a' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      `${(value as number) ?? 0} (${toPercent((value as number) ?? 0).toFixed(1)}%)`,
                      name,
                    ]}
                    cursor={{ fill: '#f4f4f5' }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={32}>
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </View>
          </>
        )}

        <View className="gap-4 rounded-xl bg-white p-4">
          <Heading3>All Feedback Requests</Heading3>

          <View className="flex-row flex-wrap gap-2">
            <Label className="mr-1 self-center text-xs text-secondary">Status:</Label>
            {(['all', 'pending', 'responded', 'rejected'] as const).map((s) => (
              <FilterChip
                key={s}
                label={s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                active={statusFilter === s}
                onPress={() => handleStatusFilter(s)}
              />
            ))}
          </View>

          {feedbackQuery.isLoading && (
            <Body className="text-center text-neutral-500">Loading...</Body>
          )}

          {!feedbackQuery.isLoading && feedbackItems.length === 0 && (
            <Body className="text-center text-neutral-500">No feedback requests found.</Body>
          )}

          {feedbackItems.length > 0 && (
            <>
              <View className="overflow-hidden rounded-lg border border-neutral-200">
                <View className="w-full flex-row justify-between bg-neutral-50 px-2 py-2">
                  <Text
                    className="min-w-16 flex-1 shrink-0 font-medium text-xs text-neutral-500"
                    numberOfLines={1}>
                    Date sent
                  </Text>
                  <Text
                    className="flex-2 shrink font-medium text-xs text-neutral-500"
                    numberOfLines={1}>
                    Realtor
                  </Text>
                  <Text
                    className="flex-2 shrink-0 font-medium text-xs text-neutral-500"
                    numberOfLines={1}>
                    Contact
                  </Text>
                  <Text
                    className="w-12 text-center font-medium text-xs text-neutral-500"
                    numberOfLines={1}>
                    # mess.
                  </Text>
                  <Text
                    className="w-12 text-center font-medium text-xs text-neutral-500"
                    numberOfLines={1}>
                    Open
                  </Text>
                  <Text
                    className="flex-1 text-right font-medium text-xs text-neutral-500"
                    numberOfLines={1}>
                    Status
                  </Text>
                </View>

                {feedbackItems.map((item) => (
                  <Pressable
                    key={item.id}
                    onPress={() =>
                      setSelectedObject({
                        title: 'Feedback Request',
                        fields: [
                          { label: 'ID', value: item.id },
                          { label: 'Realtor', value: item.realtorName },
                          { label: 'Realtor user ID', value: item.realtorUserId },
                          { label: 'Contact', value: item.contactName },
                          {
                            label: 'Last sent',
                            value: item.lastSentAt
                              ? dayjs(item.lastSentAt).format('D.M.YYYY HH:mm')
                              : null,
                          },
                          {
                            label: 'Opened',
                            value: item.openedAt
                              ? dayjs(item.openedAt).format('D.M.YYYY HH:mm')
                              : item.wasOpened
                                ? 'Yes (exact time unknown)'
                                : 'Not opened',
                          },
                          {
                            label: 'Completed',
                            value: item.completedAt
                              ? dayjs(item.completedAt).format('D.M.YYYY HH:mm')
                              : null,
                          },
                          { label: 'Messages sent', value: String(item.notificationCount) },
                          { label: 'Status', value: item.status },
                          {
                            label: 'Declined at',
                            value: item.declinedAt
                              ? dayjs(item.declinedAt).format('D.M.YYYY HH:mm')
                              : null,
                          },
                        ],
                      })
                    }
                    className="w-full flex-row items-center justify-between border-t border-neutral-100 px-2 py-3 hover:bg-neutral-100 active:bg-neutral-50">
                    <Text
                      className="min-w-16 flex-1 shrink-0 text-xs text-neutral-700"
                      numberOfLines={1}>
                      {item.lastSentAt ? dayjs(item.lastSentAt).format('D.M.YYYY') : '—'}
                    </Text>
                    <Text className="flex-2 shrink text-xs" numberOfLines={1}>
                      {item.realtorName ?? '—'}
                    </Text>
                    <Text className="flex-2 shrink-0 text-xs text-neutral-500" numberOfLines={1}>
                      {item.contactName ?? '—'}
                    </Text>
                    <Text className="w-12 shrink-0 text-center text-xs text-neutral-500">
                      {item.notificationCount}
                    </Text>
                    <View className="w-12 shrink-0 items-center">
                      <Text className="text-xs">{item.wasOpened ? '✓' : '—'}</Text>
                    </View>
                    <View className="flex-1 shrink items-end">
                      <StatusBadge status={item.status} />
                    </View>
                  </Pressable>
                ))}
              </View>

              <View className="flex-row items-center justify-between gap-4">
                <Pressable
                  onPress={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1 || feedbackQuery.isFetching}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-2 active:bg-neutral-50 disabled:opacity-40">
                  <Text className="text-sm">Previous</Text>
                </Pressable>

                <Text className="text-sm text-neutral-500">
                  Page {page} of {totalPages} ({totalCount} total)
                </Text>

                <Pressable
                  onPress={() => setPage(page + 1)}
                  disabled={page >= totalPages || feedbackQuery.isFetching}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-2 active:bg-neutral-50 disabled:opacity-40">
                  <Text className="text-sm">Next</Text>
                </Pressable>
              </View>
            </>
          )}
        </View>
      </ContentBox>

      <Dialog open={!!selectedObject} onOpenChange={(open) => !open && setSelectedObject(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedObject?.title}</DialogTitle>
          </DialogHeader>
          <ScrollView className="max-h-96">
            {selectedObject?.fields.map((field, index) => (
              <DetailRow key={index} label={field.label} value={field.value} />
            ))}
          </ScrollView>
        </DialogContent>
      </Dialog>
    </ScreenView>
  )
}
